<script setup>
    import { computed } from 'vue';

    import { useUserStore } from '@/stores/user'
    const userStore = useUserStore();

    import { useIncentivesStore } from '@/stores/incentives'
    const incentivesStore = useIncentivesStore();
    
    import { usePerformanceStore } from '@/stores/performance'
    const performanceStore = usePerformanceStore();

    import Bar from '@/components/Bar_new.vue';
    import PreviewHeader from '@/components/PreviewHeader.vue';
    import LoadingOverlay from '@/components/LoadingOverlay.vue';
    import { ref } from 'vue';
    import { useRouter } from 'vue-router';
    const router = useRouter();
    const showFullscreenLoading = ref(false);
    async function handleWidgetNavigation(to) {
        showFullscreenLoading.value = true;
        try {
            await router.push(to);
        } finally {
            showFullscreenLoading.value = false;
        }
    }
    import userAvatar from '@/components/userAvatar.vue'
    import InlineSpinner from '@/components/InlineSpinner.vue'

    const displayedPerformance = computed(() => {
        return performanceStore.performance[userStore.currentSelection.performance.display];
    })

    // Unified progress fraction (0-1) for target-line widths
    const progressFraction = computed(() => {
        const expiredPercent = displayedPerformance.value?.progress?.expired ?? 0;
        // Kaufmännisch runden (half up) bevor Umwandlung in 0-1 Fraction
        const rounded = Math.round(expiredPercent);
        return Math.min(Math.max(rounded, 0), 100) / 100; // clamp 0-100
    });

    console.log(progressFraction.value);

    // Resolve area manager (salesTeamManager) for current selected salesTeam
    const areaManagerId = computed(() => {
        if (userStore.currentSelection.scope !== 'salesTeam') return null;
        const mgr = userStore.colleagues?.find(c => c.roles?.some(r => r.name === 'SMA:salesTeamManager' && r.team?.id === userStore.currentSelection.salesTeam.id));
        return mgr?.id || null;
    });

    const wrongScope = computed(() => {
        return userStore.currentSelection.scope !== 'salesAgent';
    })

    const targetStatusIsAchieved = computed(() => {
        return performanceStore.performance['campaign']?.status?.achieved === true // to prevent status changing while animation
    })

    const targetStatusIsInactive = computed(() => {
        return (userStore.currentSelection.performance.display === 'halftime') ? true : false;
    })

    const targetStatusIsIncomplete = computed(() => {
        return !userStore.status.achieved;
    })

    const campaignAchievedComissionOnTop = computed(() => {
        // const achievedStatuses = performanceStore.performance.campaign.status.statusList.filter(status => status.achievedLatest === true)
        // if (achievedStatuses.length > 0) {
        //     // Assuming you want to return the commissionOnTop of the first achieved status
        //     return achievedStatuses[0].commissionOnTop
        // }
        return performanceStore.performance.campaign.status.commissionOnTop;
    })

    const currentStatus = computed(() => {
        return performanceStore.performance[userStore.currentSelection.performance.display].status.currentStatus.toLowerCase().replace(' ', '-');
    }) 

    const plannedStatus = computed(() => {
        return performanceStore.performance[userStore.currentSelection.performance.display].status.plannedStatus.toLowerCase().replace(' ', '-');
    }) 

    async function setDisplayStatus(display) {
        await userStore.selectDisplayStatus('performance', display);
    }

    function formatEuroValues(value) {
        return value.toLocaleString('de-DE');
    }

    function createClassNameForStatus(statusName) {
        return statusName.toLowerCase().replace(' ', '-');
    }

    const incentiveInformations = computed(() => incentivesStore.championBonusInfo);
    // Champion bonus helpers: derive from incentive data (new salesAchievements structure)
    const championEntry = computed(() => {
        // Prefer selected salesAgent, fallback to logged-in user
        const idUser = userStore.currentSelection.salesAgent?.idUser || userStore.id;
        const list = incentivesStore.incentive?.salesAgents?.champions || [];
        return list.find(c => Number(c.idUser) === Number(idUser)) || null;
    });
    function championBonusValue() {
        const entry = championEntry.value;
        return entry?.salesAchievements?.bonus?.value ?? entry?.salesBonus ?? 0;
    }
    function championBonusEntitled() {
        const entry = championEntry.value;
        return entry?.salesAchievements?.bonus?.entitled ?? entry?.salesBonusEntitled ?? false;
    }
</script>

<template>
    <div class="performance-preview-block performance">
        <preview-header title="Performance" disabled :to="{ name: 'Performance' }" button-label="Zur Liste" @click="handleWidgetNavigation({ name: 'Performance' })" />
        <LoadingOverlay :show="showFullscreenLoading" label="Daten werden geladen…" spinner-size="lg" />
        <div id="performance" :class="['widget-wrapper performance toggle', {'noValue' : !performanceStore.performance}]">
    <!-- <div id="performance" :class="['widget-wrapper performance noValue toggle', {'noValue' : !performanceStore.performance}]">
        <div>
            <router-link to="/performance">
                <div class="headline-wrapper">
                    <h2>Performance</h2>
                </div>
            </router-link>
        </div> -->
        <div class="container-wrapper" >
                <!-- <div v-if="!displayedPerformance" class="container">
                    <div class="warning" v-if="wrongScope">Bitte SalesManager auswählen</div>
                    <div class="warning" v-else >Keine Daten verfügbar!</div>
                </div> -->
                <div v-if="userStore.currentSelection.performance.isLoading === true" class="container main-content cell-grid">
                    <InlineSpinner size="lg" label="Daten werden geladen" />
                    <div class="loadingDescription">Daten werden geladen</div>
                </div>
                <template v-else-if="displayedPerformance">
                    <div :class="['container main-content', {'halftimeIsSelected' : userStore.currentSelection.performance.display === 'halftime', 'targetStatusIsAchieved' : targetStatusIsAchieved === true}]">
                        <template v-if="userStore.currentSelection.performance.display === 'campaign'">
                            <h3 class="underlined">Zielstatus</h3>
                            <div :class="['target-status-wrapper', {'targetStatusIsAchieved' : targetStatusIsAchieved}]">
                                <div class="status start" v-if="userStore.currentSelection.scope === 'salesAgent'">
                                    <userAvatar
                                        v-if="userStore.currentSelection.scope === 'salesAgent'"
                                        layout="column" :size="48" :isPerformance="true" :salesAgentId="userStore.currentSelection?.salesAgent?.id" />
                                    <userAvatar
                                        v-else-if="userStore.currentSelection.scope === 'salesTeam' && areaManagerId"
                                        layout="column" :size="48" :isPerformance="true" :idUser="areaManagerId" />
                                    <userAvatar
                                        v-else
                                        layout="column" :size="48" :isPerformance="true" :idUser="userStore.id" :isUserAvatar="true" />
                                </div>
                                <div v-if="userStore.currentSelection.scope === 'salesAgent'" class="status target">
                                    <div v-if="targetStatusIsAchieved" class="icon-wrapper"><img src="@/assets/imgs/icons/circle-check-solid.svg"></div>
                                    <div v-else class="icon-wrapper"><img src="@/assets/imgs/icons/circle-xmark-solid.svg"></div>
                                </div>
                                <div class="status goal">
                                    <userAvatar
                                        v-if="userStore.currentSelection.scope === 'salesAgent'"
                                        layout="column" :size="48" :isPerformance="true" :showPoints="false" :showPlannedStatus="true" :salesAgentId="userStore.currentSelection?.salesAgent?.id" />
                                    <userAvatar
                                        v-else-if="userStore.currentSelection.scope === 'salesTeam' && areaManagerId"
                                        layout="column" :size="48" :isPerformance="true" :showPoints="false" :showPlannedStatus="true" :idUser="areaManagerId" />
                                    <userAvatar
                                        v-else
                                        layout="column" :size="48" :isPerformance="true" :showPoints="false" :showPlannedStatus="true" :idUser="userStore.id" :isUserAvatar="true" />
                                </div>
                            </div>
                        </template>
                        <template  v-else-if="userStore.currentSelection.performance.display === 'halftime'">
                            <h3 class="underlined">Monats Ziel</h3>
                            <div :class="['target-status-wrapper', {'targetStatusIsAchieved' : targetStatusIsAchieved}]">
                                <div class="status start">
                                    <div class="sm-wrapper">
                                        <div :class="['points value-box', {'positive' : displayedPerformance.status.commissionPoints  >= 1, 'negative' : displayedPerformance.status.commissionPoints < .5}]">{{ displayedPerformance.status.commissionPoints.toFixed(2) }}</div>
                                        <div class="centeredWrapper">
                                            <div class="centered show">
                                                <p>Prämienpunkte</p>
                                            </div>
                                            <div class="centered hide">
                                                <p v-if="userStore.isSalesTeamManager || userStore.isManagement && userStore.currentSelection.scope !== 'salesAgent'">Summe Sales Manager</p>
                                                <p v-else>basierend auf Neu-Kd.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="calculated">
                                    {{ displayedPerformance.status.commissionPoints.toFixed(2) }} * {{displayedPerformance.status.statusList.find(status => status.name === displayedPerformance.status.plannedStatus).levelValue}}€
                                </div>
                                <div class="status goal month">
                                    <userAvatar
                                        v-if="userStore.currentSelection.scope === 'salesAgent'"
                                        layout="column" :size="48" :isPerformance="true" :showPoints="true" :showPlannedStatus="true" :salesAgentId="userStore.currentSelection?.salesAgent?.id" />
                                    <userAvatar
                                        v-else-if="userStore.currentSelection.scope === 'salesTeam' && areaManagerId"
                                        layout="column" :size="48" :isPerformance="true" :showPoints="true" :showPlannedStatus="true" :idUser="areaManagerId" />
                                    <userAvatar
                                        v-else
                                        layout="column" :size="48" :isPerformance="true" :showPoints="true" :showPlannedStatus="true" :idUser="userStore.id" :isUserAvatar="true" />
                                </div>
                            </div>
                        </template>
                
                        <!-- {{ displayedPerformance.status.statusList.slice(0, -1) }} -->
                        <div class="bars-wrapper">
                            <span :class="['target-line']" :style="{ width: `calc((100% - 24px) * ${progressFraction})` }" title="Verstrichene Zeit"></span>
                            <bar :class="'selling-time'" :mode="'performanceNormal'"  title="Verkaufszeit" :currentValue="displayedPerformance.progress.current" :goalValue="displayedPerformance.progress.planned"></bar >
                            <div class="container">  
                                <span :class="['target-line cell-grid']"
                                    :style="{ width: `calc(max(12px, 12px + ((100% - 24px) * ${progressFraction})))` }"
                                    title="Verstrichene Zeit"></span>
                                <bar :class="'sales-volume'" :mode="'performanceNormal'"  title="Gesamt" :isEuro=true :currentValue="displayedPerformance.salesEuro.current" :goalValue="displayedPerformance.salesEuro.planned"></bar >
                                <bar :class="'new-customer-sales' " :mode="'PointSystemNew'"  title="Neu-Kd." :isEuro=true :currentValue="displayedPerformance.salesEuroNewCustomers.current" :goalValue="displayedPerformance.salesEuroNewCustomers.planned"></bar >
                                <bar :class="'new-customer-sales-integer'" v-if="userStore.currentSelection.performance.display==='campaign'" :mode="'performanceNormal'"  title="Neu-Kd. #" :currentValue="displayedPerformance.salesNumberNewCustomers.current" :goalValue="displayedPerformance.salesNumberNewCustomers.planned"></bar >
                            </div>
                        </div>
                    </div>
                 
                    <div class="container value-wrapper" v-if="userStore.currentSelection.performance.display==='campaign'">
                        <div class="value"><h3>Status-LP<sup>*3</sup></h3> <div :class="['value-box', {'fulfilled' : displayedPerformance.status.commission > 0}]">{{formatEuroValues(displayedPerformance.status.commission)}}€</div></div>
                        <div class="value"><h3>Status-LP Incl. Touren<sup>*3</sup></h3> <div :class="['value-box']">{{formatEuroValues(campaignAchievedComissionOnTop)}}€</div></div>
                    </div>
                    <!-- <div class="container value-wrapper" v-if="userStore.currentSelection.performance.display==='campaign'">
                        <div class="value"><h3>Status-LP<sup>*3</sup></h3> <div :class="['value-box', {'fulfilled' : displayedPerformance.status.commission > 0}]">{{formatEuroValues(displayedPerformance.status.commission)}}€</div></div>
                        <div class="value"><h3>Status-LP Incl. Touren<sup>*3</sup></h3> <div :class="['value-box']">{{formatEuroValues(campaignAchievedComissionOnTop)}}€</div></div>
                    </div> -->
                    <div class="container value-wrapper" v-if="userStore.currentSelection.performance.display==='halftime'">
                        <div class="value"><h3>Monats Prämie<sup>*3</sup></h3> <div :class="['value-box', {'fulfilled' : displayedPerformance.status.commission > 0}]"> {{formatEuroValues(displayedPerformance.status.commission)}}€ </div></div>
                    </div>
                    <div class="container value-wrapper" v-if="userStore.currentSelection.performance.display==='halftime'">
                        <div class="value">
                            <h3>Champions Bonus ({{ incentivesStore.incentive.salesAgents.dateEndMonth }})<sup>*1</sup></h3> 
                            <div :class="['value-box', {'achieved': championBonusEntitled() && championBonusValue() > 0, 'not-achieved': !championBonusEntitled() && championBonusValue() > 0}]"
                                :data-bonus-value="championBonusValue()"
                                :data-bonus-entitled="championBonusEntitled()"
                            >
                                {{ formatEuroValues(championBonusValue()) }}€
                                <!-- <div class="star-box">
                                    <div class="star-1">
                                        <svg xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 784.11 815.53" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" version="1.1" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><defs></defs><g id="Layer_x0020_1"><metadata id="CorelCorpID_0Corel-Layer"></metadata><path d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z" class="fil0"></path></g></svg>
                                    </div>
                                    <div class="star-2">
                                        <svg xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 784.11 815.53" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" version="1.1" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><defs></defs><g id="Layer_x0020_1"><metadata id="CorelCorpID_0Corel-Layer"></metadata><path d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z" class="fil0"></path></g></svg>
                                    </div>
                                    <div class="star-3">
                                        <svg xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 784.11 815.53" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" version="1.1" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><defs></defs><g id="Layer_x0020_1"><metadata id="CorelCorpID_0Corel-Layer"></metadata><path d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z" class="fil0"></path></g></svg>
                                    </div>
                                    <div class="star-4">
                                        <svg xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 784.11 815.53" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" version="1.1" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><defs></defs><g id="Layer_x0020_1"><metadata id="CorelCorpID_0Corel-Layer"></metadata><path d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z" class="fil0"></path></g></svg>
                                    </div>
                                    <div class="star-5">
                                        <svg xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 784.11 815.53" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" version="1.1" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><defs></defs><g id="Layer_x0020_1"><metadata id="CorelCorpID_0Corel-Layer"></metadata><path d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z" class="fil0"></path></g></svg>
                                    </div>
                                    <div class="star-6">
                                        <svg xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 784.11 815.53" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" version="1.1" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><defs></defs><g id="Layer_x0020_1"><metadata id="CorelCorpID_0Corel-Layer"></metadata><path d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z" class="fil0"></path></g></svg>
                                    </div>
                                </div> -->
                            </div>
                        </div>
                    </div>
                </template>
                <div class="container">
                    <!-- meta bar moved above table, legacy context removed -->
                    <div class="update-info compact" role="note" aria-labelledby="incentivesInfoHeading">
                        <div class="disclaimer" role="note" aria-label="Hinweis">
                            <p id="incentiveDisclaimer" class="disclaimer-text">
                                <span class="asterisk-mark"><span aria-hidden="true">*</span><sup>3</sup></span>
                                Diese Werte dienen nur der Orientierung und ersetzen nicht die Endabrechnung aus SAP.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        <div class='wave-wrapper'>
            <div class='wave -one'></div>
            <div class='wave -two'></div>
            <div class='wave -three'></div>
        </div>
        <div class="toggle-wrapper">
            <div class="toggle-option" @click="setDisplayStatus('halftime')">
                <span class="toggle-label">Monat</span>
                <label class="switch">
                    <input type="radio" name="performance-toggle" :checked="userStore.currentSelection.performance.display==='halftime'">
                    <span class="custom-radio"></span>
                </label>
            </div>
            <div class="toggle-option" @click="setDisplayStatus('campaign')">
                <span class="toggle-label">Kampagne</span>
                <label class="switch">
                    <input type="radio" name="performance-toggle" :checked="userStore.currentSelection.performance.display==='campaign'">
                    <span class="custom-radio"></span>
                </label>
            </div>
        </div>
        </div>
    </div>
</template>

<style scoped>
/* Modern info box styling aligned with incentives */
.update-info { display:flex; flex-direction:column; gap:.45rem; border-radius:12px; background:#1e252c; font-size:.75rem; letter-spacing:.32px; }
.update-info.compact { background:linear-gradient(145deg,#1c2329,#222b33); }
.theme--light .update-info { background:#ffffff; border-color:#d5dde5; }
.disclaimer { margin:0; }
.disclaimer-text { line-height:1.45; font-size:.6rem; max-width:100%; }
.asterisk-mark { font-weight:700; color:var(--sellwerk-color-secondary,#36b4e8); font-size:.75rem; }
.theme--light .asterisk-mark { color:var(--sellwerk-color-main,#1677c5); }
.asterisk-mark sup { font-size:.65em; vertical-align:super; }
</style>